import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Eye, EyeOff } from "lucide-react"
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldError,
  FieldSeparator,
} from '@/shared/components/ui/field'
import { Input } from '@/shared/components/ui/input'
import { useAuth } from '@/auth/context/AuthContext'

interface LoginFormProps extends React.ComponentProps<"div"> {
  onSuccess?: () => void
  onLoginStart?: () => void
  onLoginError?: () => void
}

export function LoginForm({
  className,
  onSuccess,
  onLoginStart,
  onLoginError,
  ...props
}: LoginFormProps) {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setError(null)
    setIsLoading(true)

    // Trigger particle animation
    onLoginStart?.()

    try {
      // Use AuthContext login to update global state
      await login({ email, password })

      if (onSuccess) {
        onSuccess()
      } else {
        navigate("/")
      }
    } catch (err: unknown) {
      console.error("Login error:", err)
      onLoginError?.()
      if (err && typeof err === "object" && "response" in err) {
        const axiosError = err as { response?: { status?: number; data?: { detail?: string } } }
        if (axiosError.response?.status === 401) {
          setError("Credenciales incorrectas. Por favor verifica tu correo y contrasena.")
        } else if (axiosError.response?.data?.detail) {
          setError(axiosError.response.data.detail)
        } else {
          setError("Error al iniciar sesion. Por favor intenta de nuevo.")
        }
      } else {
        setError("Error de conexion. Por favor verifica tu conexion a internet.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="shadow-lg border-0">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">¡Bienvenido!</CardTitle>
          <CardDescription className="text-base">
            Ingresa tus credenciales para continuar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <FieldGroup className="gap-4">
              {/* Tonal Logo Section */}
              <Field>
                <div className="flex items-center justify-center py-1">
                  <img
                    src="/logo-tonal-negro.png"
                    alt="Tonal"
                    className="h-24 w-auto"
                  />
                </div>
              </Field>

              <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
                Iniciar sesion
              </FieldSeparator>

              {error && (
                <FieldError className="text-center bg-red-50 border border-red-200 p-3 rounded-md">
                  {error}
                </FieldError>
              )}

              <Field className="gap-1">
                <FieldLabel htmlFor="email" className="text-sm">Correo electronico</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoComplete="username"
                  className="h-12 text-base"
                />
              </Field>

              <Field className="gap-1">
                <FieldLabel htmlFor="password" className="text-sm">Contrasena</FieldLabel>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Ingrese su contrasena"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="current-password"
                    className="pr-10 h-12 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </Field>

              <Field className="items-end -mt-2">
                <Link
                  to="/forgot-password"
                  className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </Field>

              <Field>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 text-base text-white shadow-md bg-[#1e1e1e] hover:bg-[#2a2a2a] border-[#1e1e1e]"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Iniciando sesion...
                    </>
                  ) : (
                    "Iniciar Sesion"
                  )}
                </Button>
              </Field>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
